<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import walletIcon from '@/assets/icons/wallet.svg?raw'
import arrowRightIcon from '@/assets/icons/chapter-next.svg?raw'
import { useSubscription } from '@/composables/payment/composables/useSubscription'

interface UserInfo {
  memberId: string
  memberName: string
  coinBalance: number
  vipEndTime: string
  code: string
  thirdNickName: string
  thirdHeadImgUrl: string
  headImageUrl: string
  thirdPartType: string
  subscribe: boolean
  subscribeType: number
  subscribeEndDate: string
  email: string
}

interface Props {
  userInfo: UserInfo | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'topup'): void
  (e: 'details'): void
  (e: 'navigate'): void
  (e: 'signin'): void
}>()

const displayName = computed(() => {
  const u = props.userInfo
  if (!u)
    return 'Guest'
  return u.thirdNickName || u.email || u.memberName || 'Guest'
})

const uid = computed(() => props.userInfo?.code || 'XXXXXXXXX')
const coinBalance = computed(() => props.userInfo?.coinBalance ?? 0)
const hasAvatar = computed(() => !!props.userInfo?.headImageUrl)
const avatarUrl = computed(() => props.userInfo?.thirdHeadImgUrl || '')
const showSignIn = computed(() => !props.userInfo?.thirdPartType)
const isVip = computed(() => props.userInfo?.subscribe === true)

const { subscribeProducts } = useSubscription()
const showVipSection = computed(() => subscribeProducts.value.length > 0 || isVip.value)
const vipExpireText = computed(() => {
  const date = props.userInfo?.subscribeEndDate
  if (!date)
    return ''
  return `Expires ${new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
})
const popupStyle = ref<Record<string, string>>({})

function updatePosition() {
  const trigger = document.querySelector<HTMLElement>('.nav-user-trigger')
  if (!trigger)
    return
  const rect = trigger.getBoundingClientRect()
  popupStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 8}px`,
    right: `${window.innerWidth - rect.right}px`,
  }
}

onMounted(() => updatePosition())
onBeforeUnmount(() => {})
</script>

<template>
  <Teleport to="body">
    <div class="nav-user-popup" :style="popupStyle">
      <!-- 用户信息区 -->
      <div class="user-info-area">
        <div class="user-left">
          <img
            v-if="hasAvatar"
            :src="avatarUrl"
            alt="avatar"
            class="avatar"
          >
          <img
            v-else
            src="/images/rocnovel/user-default.png"
            alt="avatar"
            class="avatar"
          >
          <div class="user-text">
            <p class="user-name">
              {{ displayName }}
            </p>
            <p class="user-uid">
              UID {{ uid }}
            </p>
          </div>
        </div>
        <!-- <button v-if="showSignIn" class="sign-in-btn" @click="emit('signin')">
          Sign In
        </button> -->
      </div>

      <!-- 内容区 -->
      <div class="content-area">
        <!-- My Wallet -->
        <div class="wallet-row">
          <div class="wallet-left">
            <span class="icon" v-html="walletIcon" />
            <span class="wallet-label">My Wallet</span>
          </div>
          <div class="wallet-right">
            <span class="coin-count">{{ coinBalance }}</span>
            <span class="coin-unit">coins</span>
          </div>
        </div>

        <!-- Top Up 按钮 -->
        <button class="topup-btn" @click="emit('topup')">
          Top Up
        </button>

        <!-- VIP 卡片 -->
        <div v-if="showVipSection" class="vip-card">
          <div class="vip-text">
            <p class="vip-title">
              {{ isVip ? 'VIP Membership' : 'Get VIP Membership' }}
            </p>
            <p class="vip-sub">
              {{ isVip && vipExpireText ? vipExpireText : 'Unlimited Reading of All Stories' }}
            </p>
          </div>
          <button v-if="!isVip" class="details-btn" @click="emit('details')">
            Details
          </button>
        </div>

        <!-- Personal Center -->
        <RouterLink to="/member" class="personal-center-row" @click="emit('navigate')">
          <span>Personal Center</span>
          <span class="icon" v-html="arrowRightIcon" />
        </RouterLink>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.nav-user-popup {
  width: 383px;
  background-color: white;
  background-image: url(/images/pc/user-popup-bg.png);
  background-repeat: no-repeat;
  background-position: top left;
  background-size: 100% 100%;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0px 6px 20px 0px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  padding: 24px;
}

/* 用户信息区 */
.user-info-area {
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  padding: 20px 0 16px;
}

.user-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.avatar {
  width: 66px;
  height: 66px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.user-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.user-name {
  font-weight: 500;
  font-size: 20px;
  color: #32353A;
  margin: 0;
}

.user-uid {
  font-size: 14px;
  color: #65686F;
  margin: 0;
}

.sign-in-btn {
  width: 80px;
  height: 32px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  font-weight: 500;
  font-size: 14px;
  color: #9875FB;
  cursor: pointer;
  flex-shrink: 0;
}

/* 内容区 */
.wallet-row {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.wallet-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.wallet-label {
  font-weight: 500;
  font-size: 16px;
  color: #202124;
}

.wallet-right {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.coin-count {
  font-weight: 600;
  font-size: 16px;
  color: #202124;
}

.coin-unit {
  font-size: 12px;
  color: #65686F;
}

.topup-btn {
  width: 100%;
  height: 40px;
  background: #9875FB;
  border-radius: 20px;
  border: none;
  font-weight: 500;
  font-size: 16px;
  color: #FFFFFF;
  cursor: pointer;
  margin-top: 20px;
}

.vip-card {
  width: 100%;
  height: 80px;
  border-radius: 8px;
  background: url(/images/rocnovel/member_subscribe_bg.webp) no-repeat;
  background-size: 100% 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  box-sizing: border-box;
  margin-top: 20px;
}

.vip-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.vip-title {
  font-weight: 600;
  font-size: 15px;
  color: #32353A;
  margin: 0;
}

.vip-sub {
  font-size: 12px;
  color: #9875FB;
  margin: 0;
}

.details-btn {
  background: #9875FB;
  color: #FFFFFF;
  border-radius: 14px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
}

.personal-center-row {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.6);
  font-weight: 500;
  font-size: 16px;
  color: #65686F;
  text-decoration: none;
}

.icon {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}
</style>
